import type { StructureNode } from '@kronos/domain'

/**
 * Which nodes are on screen, and where the arrow keys go from each.
 *
 * Pure, so the keyboard model is tested rather than tried: a tree that a
 * keyboard cannot walk is a tree a screen-reader user cannot read, and an
 * off-by-one here strands focus with no visible sign.
 */

/** A node as drawn: its children are `[]` when it is collapsed. */
export interface Shown {
  readonly key: string
  readonly node: StructureNode
  readonly children: readonly Shown[]
  /** How many children are hidden by collapsing it — for the "+3" on its toggle. */
  readonly hidden: number
}

export function shownTree(root: StructureNode, collapsed: ReadonlySet<string>): Shown {
  const walk = (node: StructureNode): Shown => {
    const closed = collapsed.has(node.key)
    return {
      key: node.key,
      node,
      children: closed ? [] : node.children.map(walk),
      hidden: closed ? node.children.length : 0,
    }
  }
  return walk(root)
}

/**
 * What starts collapsed: every assembly at `depth` or deeper. Two levels open
 * is "the robot, what it is made of, and what those are made of" — the
 * sentence the screen exists for — without unfolding a few hundred fasteners
 * on first load.
 */
export function collapsedBeyond(root: StructureNode, depth: number): Set<string> {
  const out = new Set<string>()
  const walk = (node: StructureNode): void => {
    if (node.children.length > 0 && node.depth >= depth) out.add(node.key)
    node.children.forEach(walk)
  }
  walk(root)
  return out
}

/** Every assembly — "Collapse all" keeps the root open, or there is nothing to see. */
export function allAssemblies(root: StructureNode): Set<string> {
  return collapsedBeyond(root, 1)
}

export type Move = 'up' | 'down' | 'left' | 'right' | 'home' | 'end'

/** The keys that walk the tree, in both views. */
export const MOVE_KEYS: Readonly<Record<string, Move>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
  Home: 'home',
  End: 'end',
}

/** Keys from the root down to `key`, inclusive — the route a selection lights up. */
export function routeTo(root: Shown, key: string | null): ReadonlySet<string> {
  const parent = new Map<string, string>()
  const walk = (s: Shown): void => {
    for (const c of s.children) {
      parent.set(c.key, s.key)
      walk(c)
    }
  }
  walk(root)
  const out = new Set<string>()
  for (let k = key; k !== null; k = parent.get(k) ?? null) out.add(k)
  return out
}

/**
 * Where focus goes, ARIA treeview style: up and down through the visible rows
 * in reading order; right opens a closed branch or steps into an open one;
 * left closes an open branch or steps out to the parent.
 *
 * Returns the key to focus and, when the move is really an open or a close,
 * which one — the caller owns the collapsed set.
 */
export function keyMove(
  root: Shown,
  from: string,
  move: Move,
): { readonly focus: string; readonly toggle?: 'open' | 'close' } {
  const order: Shown[] = []
  const parent = new Map<string, Shown>()
  const walk = (s: Shown): void => {
    order.push(s)
    for (const c of s.children) {
      parent.set(c.key, s)
      walk(c)
    }
  }
  walk(root)
  const i = order.findIndex((s) => s.key === from)
  const here = order[i]
  if (here === undefined) return { focus: root.key }

  switch (move) {
    case 'up':
      return { focus: order[Math.max(0, i - 1)]?.key ?? from }
    case 'down':
      return { focus: order[Math.min(order.length - 1, i + 1)]?.key ?? from }
    case 'home':
      return { focus: root.key }
    case 'end':
      return { focus: order[order.length - 1]?.key ?? from }
    case 'right':
      if (here.hidden > 0) return { focus: from, toggle: 'open' }
      return { focus: here.children[0]?.key ?? from }
    case 'left':
      if (here.children.length > 0 && from !== root.key) return { focus: from, toggle: 'close' }
      return { focus: parent.get(from)?.key ?? from }
  }
}
